import React from 'react';
import { motion } from 'framer-motion';
import { Anchor, Wrench, FileText, Container, Truck, Camera, Shield } from 'lucide-react';
import { Section } from '../components/ui/Section';
import { ServiceCard } from '../components/ui/ServiceCard';

export default function Services() {  // Map each service to the corresponding specific images
  const serviceImages = [
    '/assets/images/EmptyContainer.jpg',           // Empty Container Handling & Storage
    '/assets/images/ContainerRepair.jpg',          // 24/7 Container Repair Service
    '/assets/images/ContainerSurvey.jpg',          // Container Survey & Reporting
    '/assets/images/StorageSpace.jpg',             // 2+ Hectares Storage Space
    '/assets/images/OnSiteTransportation.jpg',     // On-site Transportation
    '/assets/images/CCTV.jpg',                     // 24x7 CCTV Surveillance
    '/assets/images/ContainerCertification.jpg',   // Container Certification
    '/assets/images/Leasing.jpg',                  // Container Leasing Solutions
  ];

  const services = [
    {
      icon: Anchor,
      title: "Empty Container Handling & Storage",
      description: "Efficient handling and secure storage of your empty containers with our state-of-the-art equipment and spacious yard facilities.",
      details: [
        "Safe loading and unloading procedures",
        "Organized stacking systems",
        "Inventory management and tracking",
        "Regular container inspection during storage"
      ]
    },
    {
      icon: Wrench,
      title: "24/7 Container Repair Service",
      description: "Round-the-clock repair and maintenance services for all types of containers, ensuring they meet industry standards and are ready for use.",
      details: [
        "Structural repairs",
        "Panel replacement",
        "Floor repairs",
        "Door hardware servicing",
        "Specialized reefer container repairs"
      ]
    },
    {
      icon: FileText,
      title: "Container Survey & Reporting",
      description: "Comprehensive inspection and detailed reporting on container conditions to help you make informed decisions about repairs and maintenance.",
      details: [
        "Pre-lease inspections",
        "Damage assessment",
        "Detailed photographic documentation",
        "Cost estimation for repairs",
        "Digital reports available 24/7"
      ]
    },
    {
      icon: Container,
      title: "2+ Hectares Storage Space",
      description: "Expansive storage area strategically located near major ports to accommodate all your container storage requirements with ease.",
      details: [
        "Organized storage zones",
        "Efficient space utilization",
        "Easy access for quick retrieval",
        "Separate areas for different container types"
      ]
    },
    {
      icon: Truck,
      title: "On-site Transportation",
      description: "Seamless movement of containers within our facility using specialized equipment like top lifters and hydra cranes for safe handling.",
      details: [
        "Top lifter operations",
        "Hydra crane services",
        "Skilled equipment operators",
        "Regular equipment maintenance",
        "Safe container positioning"
      ]
    },
    {
      icon: Camera,
      title: "24x7 CCTV Surveillance",
      description: "Complete security with continuous monitoring of the entire yard premises to ensure the safety of your valuable assets.",
      details: [
        "High-definition camera coverage",
        "24/7 monitoring",
        "Secured footage storage",
        "Regular security patrols",
        "Controlled access to premises"
      ]
    },
    {
      icon: Shield,
      title: "Container Certification",
      description: "Professional certification services to ensure your containers meet international standards and compliance requirements.",
      details: [
        "CSC certification assistance",
        "Standard compliance verification",
        "Documentation management",
        "Renewal of certifications",
        "Classification guidance"
      ]
    },
    {
      icon: Container,
      title: "Container Leasing Solutions",
      description: "Flexible container leasing options to support your logistics and shipping needs without the burden of ownership.",
      details: [
        "Short and long-term leases available",
        "Variety of container types",
        "Competitive pricing",
        "Maintenance included",
        "Delivery arrangements"
      ]
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <div className="relative py-24 bg-primary">
        <div className="absolute inset-0 overflow-hidden">          <img
            src="/assets/images/Photo1.jpg"
            alt="Container services"
            className="w-full h-full object-cover opacity-20"
            onError={(e) => {
              // Fallback to Photo2, then external image
              if (e.currentTarget.src.includes('Photo1.jpg')) {
                e.currentTarget.src = "/assets/images/Photo2.jpg";
                console.log("Services hero image fallback: trying Photo2");
              } else if (e.currentTarget.src.includes('Photo2.jpg')) {
                e.currentTarget.src = "https://images.unsplash.com/photo-1565358795042-526f1881723f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80";
                console.log("Services hero image fallback: trying external image");
              } else {
                console.warn("Services hero image failed completely");
              }
            }}
            onLoad={() => {
              console.log("Services hero image loaded successfully");
            }}
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center text-white">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
            >
              Our Services
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl"
            >
              Comprehensive container handling solutions for all your shipping needs
            </motion.p>
          </div>
        </div>
      </div>

      {/* Services Overview */}
      <Section
        subtitle="At Mega Marine Container Terminals, we provide a wide range of services designed to meet the diverse needs of shipping lines, logistics companies, and container leasing firms."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 xl:gap-10">
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              icon={<service.icon className="h-8 w-8" />}
              title={service.title}
              description={service.description}
              details={service.details}
              imageSrc={serviceImages[index]}
              index={index}
            />
          ))}
        </div>
      </Section>

      {/* Services Navigation Bar */}
      <div className="sticky top-32 z-30 bg-white/95 backdrop-blur-sm border-b border-gray-200 py-3 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-gray-900">Service Details</h4>
            <div className="flex items-center gap-2 overflow-x-auto">
              {services.map((service, index) => (
                <button
                  key={index}
                  onClick={() => {
                    const element = document.getElementById(`service-detail-${index}`);
                    if (element) {
                      const navbarHeight = 96; // Account for main navbar height
                      const stickyNavHeight = 60; // Account for this sticky nav height
                      const totalOffset = navbarHeight + stickyNavHeight;
                      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                      window.scrollTo({
                        top: elementPosition - totalOffset,
                        behavior: 'smooth'
                      });
                    }
                  }}
                  className="whitespace-nowrap px-3 py-1.5 text-sm rounded-full border border-gray-300 hover:border-primary hover:text-primary transition-colors duration-300"
                >
                  {service.title.split(' ').slice(0, 2).join(' ')}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Service Sections */}
      <div className="bg-gray-50">
        {services.map((service, index) => (
          <Section
            key={index}
            id={`service-detail-${index}`}
            className="py-16 border-b border-gray-200 last:border-b-0"
          >
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Service Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={index % 2 === 0 ? "lg:order-1" : "lg:order-2"}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center text-white">
                    <service.icon className="h-8 w-8" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-3xl font-bold text-gray-900">{service.title}</h3>
                    <div className="w-12 h-1 bg-ocean mt-2"></div>
                  </div>
                  <button
                    onClick={() => {
                      const servicesGrid = document.querySelector('.grid.md\\:grid-cols-2.lg\\:grid-cols-3');
                      if (servicesGrid) {
                        const navbarHeight = 96; // Account for main navbar height
                        const elementPosition = servicesGrid.getBoundingClientRect().top + window.pageYOffset;
                        window.scrollTo({
                          top: elementPosition - navbarHeight - 20, // Extra 20px padding
                          behavior: 'smooth'
                        });
                      }
                    }}
                    className="hidden lg:flex items-center gap-2 text-primary hover:text-primary-dark transition-colors duration-300 font-medium"
                  >
                    ↑ Back to Services
                  </button>
                </div>
                
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  {service.description}
                </p>
                
                <div className="space-y-4">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">Key Features:</h4>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {service.details.map((detail, detailIndex) => (
                      <motion.div
                        key={detailIndex}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: detailIndex * 0.1 }}
                        className="flex items-start gap-3"
                      >
                        <div className="w-2 h-2 bg-ocean rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-700">{detail}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
                
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="mt-8"
                >
                  <a
                    href="/contact"
                    className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-medium transition-colors duration-300"
                  >
                    Get Quote for This Service
                    <motion.span
                      animate={{ x: [0, 4, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      →
                    </motion.span>
                  </a>
                </motion.div>
              </motion.div>
              
              {/* Service Image */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className={index % 2 === 0 ? "lg:order-2" : "lg:order-1"}
              >
                <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                  <img
                    src={serviceImages[index]}
                    alt={service.title}
                    className="w-full h-[400px] object-cover hover:scale-105 transition-transform duration-700"
                    onError={(e) => {
                      // Fallback to a default image if service image fails
                      e.currentTarget.src = "/assets/images/Photo1.jpg";
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-white/90 backdrop-blur-sm rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Professional Service</h4>
                      <p className="text-sm text-gray-600">
                        Trusted by major shipping lines and logistics companies worldwide
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </Section>
        ))}
      </div>

      {/* Call to Action */}
      <Section className="bg-primary text-white">
        <div className="text-center max-w-3xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-3xl md:text-4xl font-bold mb-6"
          >
            Need Custom Container Services?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-xl mb-8 text-gray-200"
          >
            Our team is ready to discuss your specific requirements and provide tailored solutions for your business needs.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-col sm:flex-row justify-center gap-4"
          >
            <a 
              href="/contact" 
              className="bg-white text-primary hover:bg-gray-100 px-8 py-3 rounded-md font-medium transition-colors duration-300 inline-flex items-center justify-center"
            >
              Contact Our Team
            </a>
            <a 
              href="/contact" 
              className="bg-transparent border border-white text-white hover:bg-white/10 px-8 py-3 rounded-md font-medium transition-colors duration-300 inline-flex items-center justify-center"
            >
              Visit Our Locations
            </a>
          </motion.div>
        </div>
      </Section>
    </div>
  );
}
